import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheBoatDbApi } from '../../providers/useTheBoatDbApi';
import { useApiKeysQueryKey } from './useApiKeysQuery';

/**
 * Revokes a key by id.
 *
 * `mutate(keyId)` rather than `mutate({ path: { keyId } })`: the generated
 * mutation takes the endpoint's parameter object, which is the right shape for
 * the transport and the wrong shape for a call site that has one id in hand.
 *
 * The 204 carries no body, so there is nothing to return. The list is
 * invalidated rather than filtered locally — a revoke that raced another tab
 * should leave the screen agreeing with the server, not with its own optimism.
 */
export const useRevokeApiKeyMutation = () => {
  const { tanstackApiClient } = useTheBoatDbApi();
  const queryClient = useQueryClient();
  const keysQueryKey = useApiKeysQueryKey();

  // Function and key only — see `useCreateApiKeyMutation` for why the rest of
  // the generated options cannot come along when the variables shape changes.
  const { mutationFn, mutationKey } = tanstackApiClient.mutation('delete', '/v1/keys/{keyId}').mutationOptions;

  return useMutation({
    mutationKey,
    mutationFn: async (keyId: string) => {
      await mutationFn({ path: { keyId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keysQueryKey });
    },
  });
};
